import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { useMemo } from "react";
import { getAgentById } from "../../api/client";
import { useAgents } from "../../hooks/useAgents";
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import { useS3Upload } from "../../hooks/useS3Upload";
import SkillSelector from "../../components/Agents/SkillSelector";
import AgentStatusToggle from "../../components/Agents/AgentStatusToggle";
import AgentDocumentsSection from "../../components/Agents/AgentDocumentsSection";

const MODEL_OPTIONS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini (rápido e econômico)" },
  { value: "gpt-4.1", label: "GPT-4.1" },
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
];

// Limites usados pelo RAG do backend (agentRagService / faqSkill)
const MAX_RAG_DOCS = 8; // loadAgentKnowledgeChunks: documents.slice(0, 8)
const MAX_RAG_URLS = 6; // loadAgentKnowledgeChunks: urls.slice(0, 6)

/** Converte status do backend para boolean (toggle). */
const statusToBoolean = (v) => v === "active" || v === true;

/** Converte boolean para status do backend. */
const booleanToStatus = (v) => (v ? "active" : "inactive");

/** Normaliza skills do backend (array de { skill_key, config?, order_index? }) para o form. */
const normalizeSkillsFromBackend = (raw) => {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s) => (typeof s === "object" && s?.skill_key ? { ...s } : null))
    .filter(Boolean)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
};

const AgentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);
  const canUpdate = can(scopeSet, "agents", "update");
  const canCreate = can(scopeSet, "agents", "create");
  const readOnly = isEdit && !canUpdate;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [engineType, setEngineType] = useState("xpro"); // 'xpro' ou 'n8n'
  const [model, setModel] = useState("gpt-4o-mini");
  const [temperature, setTemperature] = useState(0.7);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [n8nWebhookUrl, setN8nWebhookUrl] = useState("");
  const [n8nHeadersJson, setN8nHeadersJson] = useState("{\n  \"X-API-KEY\": \"seu-token-aqui\"\n}");
  const [status, setStatus] = useState(true);
  const [skills, setSkills] = useState([]);
  const [sourceUrls, setSourceUrls] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [documents, setDocuments] = useState([]); // { id, file?, url?, name }[]
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [submitError, setSubmitError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const fileInputRef = React.useRef(null);
  const documentIdRef = React.useRef(0);

  // O backend usa `mammoth` para DOCX. O formato `.doc` legacy tende a falhar/retornar texto vazio.
  // Por isso, evitamos o upload de `.doc` no frontend.
  const ACCEPT_DOCS = ".pdf,.docx,.xls,.xlsx";
  const { uploadMany: uploadManyDocs } = useS3Upload();

  const buildDocumentsPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `agents/documents/${yyyy}/${mm}`;
  };

  const handleAddUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    try {
      new URL(url);
    } catch {
      setSnack({ open: true, severity: "warning", message: "Informe uma URL válida." });
      return;
    }
    setSourceUrls((prev) => {
      if (prev.includes(url)) return prev;
      if (prev.length >= MAX_RAG_URLS) {
        setSnack({
          open: true,
          severity: "warning",
          message: `Limite de ${MAX_RAG_URLS} links de sites por agente. Remova links antes de adicionar mais.`,
        });
        return prev;
      }
      return [...prev, url];
    });
    setNewUrl("");
  };

  const handleRemoveUrl = (index) => {
    setSourceUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const validateDocFiles = (fileList) => {
    const exts = [".pdf", ".docx", ".xls", ".xlsx"];
    const accepted = [];
    const errors = [];
    const MAX_MB = 15;
    Array.from(fileList || []).forEach((f) => {
      const name = (f && f.name) || "";
      const ext = name.toLowerCase().slice(name.lastIndexOf("."));
      if (ext === ".doc") {
        errors.push(`${name || "arquivo"}: formato .doc legacy não suportado. Use .docx.`);
        return;
      }
      const sizeOk = f && typeof f.size === "number" && f.size <= MAX_MB * 1024 * 1024;
      if (!exts.includes(ext)) {
        errors.push(`${name || "arquivo"}: tipo inválido (apenas PDF, Word (.docx) e planilhas).`);
      } else if (!sizeOk) {
        errors.push(`${name || "arquivo"}: maior que ${MAX_MB}MB.`);
      } else {
        accepted.push(f);
      }
    });
    if (errors.length) {
      setSnack({ open: true, severity: "warning", message: errors.slice(0, 2).join(" ") });
    }
    return accepted;
  };

  const addDocFiles = (fileList) => {
    const files = validateDocFiles(fileList);
    if (!files.length) return;

    const availableSlots = Math.max(0, MAX_RAG_DOCS - documents.length);
    if (availableSlots <= 0) {
      setSnack({
        open: true,
        severity: "warning",
        message: `Limite de ${MAX_RAG_DOCS} documentos por agente. Remova documentos antes de adicionar mais.`,
      });
      return;
    }

    const trimmed = files.slice(0, availableSlots);
    if (trimmed.length !== files.length) {
      setSnack({
        open: true,
        severity: "warning",
        message: `Foram ignorados ${files.length - trimmed.length} documentos por exceder o limite (${MAX_RAG_DOCS}).`,
      });
    }
    setDocuments((prev) => [
      ...prev,
      ...trimmed.map((file) => ({
        id: `tmp_${++documentIdRef.current}`,
        file,
        name: file.name,
      })),
    ]);
  };

  const handleClickAddDocs = () => fileInputRef.current?.click();

  const handleDropDocs = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addDocFiles(e.dataTransfer.files);
  };

  const handleDragOverDocs = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const moveDocument = (idx, dir) => {
    setDocuments((prev) => {
      const list = [...prev];
      const to = idx + dir;
      if (to < 0 || to >= list.length) return prev;
      const [it] = list.splice(idx, 1);
      list.splice(to, 0, it);
      return list;
    });
  };

  const removeDocument = (idx) => {
    setDocuments((prev) => prev.filter((_, i) => i !== idx));
  };

  const { data: agentData, isLoading: loadingAgent } = useQuery(
    ["agent", id],
    () => getAgentById(id),
    { enabled: isEdit }
  );

  const agent = agentData?.data ?? agentData;

  useEffect(() => {
    if (!agent || !isEdit) return;
    setName(agent.name ?? agent.nome ?? "");
    setDescription(agent.description ?? agent.descricao ?? "");
    const agentModel = agent.model ?? agent.modelo ?? "gpt-4o-mini";
    setModel(agentModel);
    setTemperature(Number(agent.temperature) ?? 0.7);
    const rawSystemPrompt = agent.system_prompt ?? agent.prompt ?? agent.prompt_base ?? "";
    // Detecta agente N8N pelo model
    if (String(agentModel).toLowerCase() === "n8n") {
      setEngineType("n8n");
      // Para N8N, esperamos JSON em system_prompt com config do webhook
      try {
        const parsed = typeof rawSystemPrompt === "string" ? JSON.parse(rawSystemPrompt || "{}") : rawSystemPrompt || {};
        if (parsed && typeof parsed === "object") {
          if (parsed.webhook_url) setN8nWebhookUrl(parsed.webhook_url);
          if (parsed.headers) {
            try {
              setN8nHeadersJson(JSON.stringify(parsed.headers, null, 2));
            } catch {
              setN8nHeadersJson("{\n  \"X-API-KEY\": \"seu-token-aqui\"\n}");
            }
          }
        }
      } catch {
        // Se não for JSON válido, mantém campos padrão
      }
      // Para agentes N8N o system_prompt é usado como config JSON, então não exibimos no campo de texto
      setSystemPrompt("");
    } else {
      setEngineType("xpro");
      setSystemPrompt(rawSystemPrompt);
    }
    setStatus(statusToBoolean(agent.status ?? agent.enabled ?? agent.is_active));
    setSkills(normalizeSkillsFromBackend(agent.skills));
    const urls = agent.source_urls ?? agent.urls ?? agent.reference_urls;
    setSourceUrls(Array.isArray(urls) ? urls.filter(Boolean) : []);
    const docs = agent.documents ?? agent.documentos ?? [];
    setDocuments(
      Array.isArray(docs)
        ? docs.map((d) => ({
            id: d.id,
            url: d.file_url ?? d.url,
            name: d.file_name ?? d.name ?? "documento",
          }))
        : []
    );
  }, [agent, isEdit]);

  useEffect(() => {
    const onAddFiles = (ev) => {
      const files = ev.detail || [];
      addDocFiles(files);
    };
    window.addEventListener("agent-documents:addFiles", onAddFiles);
    return () => window.removeEventListener("agent-documents:addFiles", onAddFiles);
  }, []);

  const { createMutation, updateMutation } = useAgents();
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Validações de consistência com o RAG do backend.
    // Isso evita criar/editar um agente "configurado", mas que não consegue extrair conhecimento.
    if (engineType === "xpro") {
      if (documents.length > MAX_RAG_DOCS) {
        const msg = `Você excedeu o limite de ${MAX_RAG_DOCS} documentos. O FAQ só considera os primeiros ${MAX_RAG_DOCS}. Remova os excedentes.`;
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }

      if (sourceUrls.length > MAX_RAG_URLS) {
        const msg = `Você excedeu o limite de ${MAX_RAG_URLS} links de sites. O FAQ só considera os primeiros ${MAX_RAG_URLS}. Remova os excedentes.`;
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }

      const hasLegacyDoc = documents.some((d) => String(d?.name || "").toLowerCase().endsWith(".doc"));
      if (hasLegacyDoc) {
        const msg = "Existem documentos no formato .doc legacy. O RAG do FAQ usa mammoth e recomenda .docx. Remova/substitua por .docx.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }
    }

    if (engineType === "n8n") {
      const url = String(n8nWebhookUrl || "").trim();
      if (!url) {
        const msg = "Informe a URL do webhook N8N.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }
      try {
        // valida formato básico da URL
        new URL(url);
      } catch {
        const msg = "URL do webhook N8N inválida.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }
      if (n8nHeadersJson) {
        try {
          JSON.parse(n8nHeadersJson);
        } catch {
          const msg = "Headers JSON inválidos. Corrija o JSON ou deixe em branco.";
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }
      }
    }

    const buildPayload = (docUrls) => {
      // Monta system_prompt / modelo conforme o tipo do agente
      let finalModel = model;
      let finalSystemPrompt = systemPrompt.trim() || null;

      if (engineType === "n8n") {
        finalModel = "n8n";
        let headersObj = {};
        try {
          headersObj = n8nHeadersJson ? JSON.parse(n8nHeadersJson) : {};
        } catch {
          headersObj = {};
        }
        finalSystemPrompt = JSON.stringify(
          {
            webhook_url: n8nWebhookUrl?.trim() || "",
            headers: headersObj,
            timeout_ms: 15000,
          },
          null,
          2
        );
      }

      return {
        name: name.trim() || (agent?.nome ?? agent?.name),
        description: description.trim() || null,
        status: booleanToStatus(status),
        model: finalModel,
        temperature,
        system_prompt: finalSystemPrompt,
        skills: Array.isArray(skills) ? skills : [],
        source_urls: sourceUrls.length ? sourceUrls.filter((u) => u && String(u).trim()) : undefined,
        documents: docUrls.length ? docUrls : undefined,
      };
    };

    const novos = documents.filter((d) => d.file);
    let uploadResults = [];

    if (novos.length) {
      try {
        setUploadProgress({ done: 0, total: novos.length });
        uploadResults = await uploadManyDocs(
          novos.map((n) => n.file),
          { prefix: buildDocumentsPrefix() },
          (done, total) => setUploadProgress({ done, total })
        );
        uploadResults.forEach((r, i) => {
          if (!r?.ok || !r?.url) throw new Error(r?.error || `Falha no upload de "${novos[i]?.name}"`);
        });
      } catch (err) {
        setSubmitError(err?.message || "Falha ao enviar documentos.");
        setSnack({ open: true, severity: "error", message: err?.message || "Falha ao enviar documentos." });
        return;
      } finally {
        setUploadProgress({ done: 0, total: 0 });
      }
    }

    const docUrls = documents.map((d) => {
      if (d.url) return { url: d.url, file_name: d.name };
      const i = novos.indexOf(d);
      if (i === -1) return null;
      const r = uploadResults[i];
      return r?.url ? { url: r.url, file_name: d.name } : null;
    }).filter(Boolean);

    const payload = buildPayload(docUrls);

    if (isEdit) {
      updateMutation.mutate(
        { id, data: payload },
        {
          onSuccess: () => {
            setSnack({ open: true, severity: "success", message: "Agente de IA atualizado." });
            navigate(`/agents/${id}`);
          },
          onError: (err) => {
            const msg = err?.message || "Erro ao atualizar o agente de IA.";
            setSubmitError(msg);
            setSnack({ open: true, severity: "error", message: msg });
          },
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (res) => {
          const created = res?.data ?? res;
          const newId = created?.id ?? created?.id_agent;
          setSnack({ open: true, severity: "success", message: "Agente de IA criado." });
          if (newId) navigate(`/agents/${newId}`);
          else navigate("/agents");
        },
        onError: (err) => {
          const msg = err?.message || "Erro ao criar o agente de IA.";
          setSubmitError(msg);
          setSnack({ open: true, severity: "error", message: msg });
        },
      });
    }
  };

  if (isEdit && loadingAgent) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Carregando agente de IA...</Typography>
      </Box>
    );
  }

  const sectionTitleSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1.5,
    fontWeight: 600,
    fontSize: "1rem",
    color: "text.primary",
  };

  return (
    <Box className="tour-agent-form" sx={{ paddingTop: 1, paddingBottom: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 3 },
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/agents")}>
            Voltar
          </Button>
          {isEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<BarChartRoundedIcon />}
              onClick={() => navigate(`/agents/${id}/analytics`)}
            >
              Estatísticas
            </Button>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {isEdit ? "Editar agente de IA" : "Novo agente de IA"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isEdit
            ? "Altere os dados abaixo e salve quando terminar."
            : "Preencha o nome e as capacidades. O resto é opcional."}
        </Typography>

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
          {/* 1. Identificação */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography sx={sectionTitleSx}>
              <BadgeOutlinedIcon fontSize="small" color="primary" />
              Identificação
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Nome do agente de IA"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                size="small"
                placeholder="Ex.: Atendente de vendas"
                helperText="Um nome que você reconheça na lista de agentes de IA"
                inputProps={{ readOnly }}
              />
              <TextField
                label="Descrição (opcional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
                size="small"
                placeholder="Ex.: Atende clientes e tira dúvidas sobre produtos"
                inputProps={{ readOnly }}
              />
            </Stack>
          </Paper>

          {/* 2. Capacidades e status */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography sx={sectionTitleSx}>
              <PsychologyOutlinedIcon fontSize="small" color="primary" />
              Capacidades
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>Tipo de agente</InputLabel>
                <Select
                  label="Tipo de agente"
                  value={engineType}
                  onChange={(e) => setEngineType(e.target.value)}
                >
                  <MenuItem value="xpro">Agente Xpro (modelo de IA interno)</MenuItem>
                  <MenuItem value="n8n">Agente N8N (fluxo externo)</MenuItem>
                </Select>
              </FormControl>
              {engineType === "xpro" && (
                <SkillSelector value={skills} onChange={setSkills} disabled={readOnly} />
              )}
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Agente de IA ativo?
                </Typography>
                <AgentStatusToggle value={status} onChange={setStatus} disabled={isEdit && !canUpdate} />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Inativo = não aparece em solicitações até você ativar.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* 3. Fontes de conhecimento */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography sx={sectionTitleSx}>
              <MenuBookOutlinedIcon fontSize="small" color="primary" />
              Fontes de conhecimento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Documentos e links que o agente de IA usa para responder. Tudo opcional.
            </Typography>
            {engineType === "n8n" && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Para agentes N8N, documentos/URLs não são enviados automaticamente para o seu fluxo. Use-os apenas se o seu
                N8N buscar essas informações por conta própria.
              </Alert>
            )}
            <Stack spacing={2}>
              <AgentDocumentsSection
                documents={documents}
                fileInputRef={fileInputRef}
                handleClickAdd={handleClickAddDocs}
                handleDrop={handleDropDocs}
                handleDragOver={handleDragOverDocs}
                moveDocument={moveDocument}
                removeDocument={removeDocument}
                readOnly={readOnly}
                accept={ACCEPT_DOCS}
                embedded
              />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  <LinkIcon fontSize="small" />
                  Links de sites
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="https://..."
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddUrl())}
                    disabled={readOnly}
                    sx={{ minWidth: 260, flex: 1 }}
                  />
                  <Button variant="outlined" size="small" startIcon={<AddRoundedIcon />} onClick={handleAddUrl} disabled={readOnly}>
                    Adicionar
                  </Button>
                </Stack>
                {sourceUrls.length > 0 && (
                  <List dense disablePadding sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
                    {sourceUrls.map((url, index) => (
                      <ListItem key={`${url}-${index}`}>
                        <ListItemText primary={url} primaryTypographyProps={{ variant: "body2", noWrap: true }} />
                        {!readOnly && (
                          <ListItemSecondaryAction>
                            <IconButton size="small" edge="end" onClick={() => handleRemoveUrl(index)} aria-label="Remover URL">
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Stack>
          </Paper>

          {/* 4. Avançado */}
          <Accordion
            defaultExpanded={false}
            sx={{
              boxShadow: "none",
              "&:before": { display: "none" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, fontWeight: 500 }}>
                <SettingsOutlinedIcon fontSize="small" />
                Configurações avançadas
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ flexDirection: "column", gap: 2, display: "flex", pt: 0 }}>
              {engineType === "xpro" && (
                <>
                  <FormControl fullWidth size="small" disabled={readOnly}>
                    <InputLabel>Modelo de IA</InputLabel>
                    <Select label="Modelo de IA" value={model} onChange={(e) => setModel(e.target.value)}>
                      {MODEL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box>
                    <Typography gutterBottom variant="body2">
                      Criatividade (temperatura)
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, v) => setTemperature(v)}
                      min={0}
                      max={2}
                      step={0.1}
                      valueLabelDisplay="auto"
                      disabled={readOnly}
                      marks={[
                        { value: 0, label: "0" },
                        { value: 1, label: "1" },
                        { value: 2, label: "2" },
                      ]}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Mais alto = respostas mais variadas.
                    </Typography>
                  </Box>
                  <TextField
                    label="Instruções para o agente de IA"
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    placeholder="Ex.: Você é um atendente prestativo. Responda em português de forma clara."
                    helperText="Opcional — tom e regras gerais"
                    inputProps={{ readOnly }}
                  />
                </>
              )}
              {engineType === "n8n" && (
                <>
                  <TextField
                    label="URL do webhook N8N"
                    value={n8nWebhookUrl}
                    onChange={(e) => setN8nWebhookUrl(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="https://seu-n8n.com/webhook/xpro-atendimento"
                    helperText="Endpoint HTTP do fluxo N8N que vai receber as mensagens desta conversa."
                    inputProps={{ readOnly }}
                  />
                  <TextField
                    label="Headers JSON para o N8N (opcional)"
                    value={n8nHeadersJson}
                    onChange={(e) => setN8nHeadersJson(e.target.value)}
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    placeholder='{\n  "X-API-KEY": "seu-token-aqui"\n}'
                    helperText="Headers adicionais enviados na requisição para o N8N (formato JSON)."
                    inputProps={{ readOnly }}
                  />
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Como configurar o fluxo no N8N
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      1. Crie um fluxo com um nó Webhook usando exatamente a URL configurada acima.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      2. O webhook recebe um JSON com: <code>conversationId</code>, <code>message</code>, <code>agent</code>, <code>meta</code> e <code>company</code>.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      3. Processe a mensagem no fluxo (nós de função, HTTP, banco, etc.) e, no final, faça o nó de resposta HTTP devolver um JSON com
                      um dos campos: <code>reply</code>, <code>output</code> ou <code>message</code> contendo o texto que será enviado ao cliente.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      4. Garanta que o fluxo responda sempre com HTTP 2xx para o Xpro considerar a chamada bem sucedida.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      O agente N8N ignora modelo de IA e temperatura locais; toda a lógica e integrações ficam dentro do fluxo no N8N.
                    </Typography>
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Ações */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap", mt: 3 }}>
            <Button type="button" variant="outlined" onClick={() => navigate("/agents")} disabled={isSaving}>
              {isEdit ? "Voltar" : "Cancelar"}
            </Button>
            {(isEdit ? canUpdate : canCreate) && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSaving || !name.trim() || (uploadProgress.total > 0 && uploadProgress.done < uploadProgress.total)}
              >
                {uploadProgress.total > 0 ? (
                  `Enviando ${uploadProgress.done}/${uploadProgress.total}...`
                ) : isSaving ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Salvando...
                  </>
                ) : isEdit ? (
                  "Salvar alterações"
                ) : (
                  "Criar agente de IA"
                )}
              </Button>
            )}
          </Box>
        </Stack>
      </form>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default AgentForm;
